// src/components/DocumentPDF.jsx
// Génération PDF côté navigateur — jsPDF + jsPDF-AutoTable
// Architecture : autoTable gère 100% la pagination via margin.top + didDrawPage

// ── Chargement dynamique des libs ─────────────────────────────
async function loadLibs() {
  if (!window.jspdf) {
    await new Promise((res, rej) => {
      const s = document.createElement('script')
      s.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js'
      s.onload = res; s.onerror = rej
      document.head.appendChild(s)
    })
  }
  if (!window.jspdf?.jsPDF?.API?.autoTable) {
    await new Promise((res, rej) => {
      const s = document.createElement('script')
      s.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js'
      s.onload = res; s.onerror = rej
      document.head.appendChild(s)
    })
  }
  return window.jspdf.jsPDF
}

// ── Constantes mise en page ────────────────────────────────────
const PAGE_W = 210
const PAGE_H = 297
const ML = 14   // marge gauche
const MR = 14   // marge droite
const MB = 22   // marge bas (footer)
const COL_W = PAGE_W - ML - MR  // 182 mm

// Hauteurs des headers (marges top pour autoTable)
const HEADER_H_P1 = 105  // page 1 : grand header (bande + émetteur + client + infos)
const HEADER_H_NEXT = 18   // pages suivantes : mini header

// ── Palette couleurs ───────────────────────────────────────────
const C = {
  primary: [30, 100, 168],
  primaryLight: [235, 243, 252],
  accent: [0, 168, 120],
  text: [25, 35, 50],
  textMid: [90, 105, 120],
  textLight: [140, 155, 170],
  border: [215, 225, 235],
  rowOdd: [247, 250, 253],
  white: [255, 255, 255],
  danger: [210, 50, 60],
  dangerLight: [254, 235, 237],
}

const STATUT_COLORS = {
  brouillon: [180, 180, 180],
  envoye: [30, 100, 168],
  accepte: [0, 150, 100],
  refuse: [210, 50, 60],
  en_attente: [200, 130, 0],
  partiel: [200, 130, 0],
  regle: [0, 150, 100],
  annule: [150, 50, 50],
  transforme: [100, 80, 160],
}

const TYPES_FR = {
  devis: 'DEVIS',
  facture: 'FACTURE',
  bon_commande: 'BON DE COMMANDE',
  bon_livraison: 'BON DE LIVRAISON',
  avoir: 'AVOIR',
  facture_acompte: "FACTURE D'ACOMPTE",
}

const STATUTS_FR = {
  brouillon: 'Brouillon', envoye: 'Envoyé',
  accepte: 'Accepté', refuse: 'Refusé',
  en_attente: 'En attente', partiel: 'Partiel',
  regle: 'Réglé', annule: 'Annulé',
  transforme: 'Transformé',
}

const DEVISE_SYMBOLS = { EUR: 'EUR', XOF: 'FCFA', USD: 'USD', GBP: 'GBP', MAD: 'MAD' }

// ── Helpers ────────────────────────────────────────────────────
const fmt = n => {
  const [i, d] = (parseFloat(n) || 0).toFixed(2).split('.')
  return i.replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ',' + d
}
const fmtDate = d => {
  if (!d) return '-'
  const dt = new Date(d)
  return [dt.getDate(), dt.getMonth() + 1, dt.getFullYear()]
    .map((v, idx) => idx < 2 ? String(v).padStart(2, '0') : v).join('/')
}
const fmtMoney = (n, devise) =>
  fmt(n) + '\u00A0' + (DEVISE_SYMBOLS[devise] || devise || 'EUR')

const rgb2hex = ([r, g, b]) =>
  '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('')

// ══════════════════════════════════════════════════════════════
//  GÉNÉRATEUR PRINCIPAL
// ══════════════════════════════════════════════════════════════
export async function generateDocumentPDF(doc, mode = 'download') {
  const JsPDF = await loadLibs()
  const pdf = new JsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
  const devise = doc.monnaie || 'EUR'
  const typeLabel = TYPES_FR[doc.type] || doc.type?.toUpperCase() || 'DOCUMENT'

  // Charger le logo en base64 si disponible
  if (doc.user_logo_url) {
    try {
      const resp = await fetch(doc.user_logo_url)
      const blob = await resp.blob()
      doc._logoB64 = await new Promise(res => {
        const r = new FileReader()
        r.onload = () => res(r.result)
        r.readAsDataURL(blob)
      })
      doc._logoFmt = blob.type === 'image/png' ? 'PNG' : 'JPEG'
    } catch { /* pas de logo, on continue */ }
  }

  // ── Raccourcis dessin ───────────────────────────────────────
  const fill = ([r, g, b]) => pdf.setFillColor(r, g, b)
  const draw = ([r, g, b]) => pdf.setDrawColor(r, g, b)
  const clr = ([r, g, b]) => pdf.setTextColor(r, g, b)
  const font = (style = 'normal', size = 9) => { pdf.setFont('helvetica', style); pdf.setFontSize(size) }
  const tx = (t, x, y, opts = {}) => pdf.text(String(t ?? ''), x, y, opts)
  const rect = (x, y, w, h, col) => { fill(col); pdf.rect(x, y, w, h, 'F') }
  const hline = (y, x1 = ML, x2 = PAGE_W - MR, col = C.border, lw = 0.2) => {
    draw(col); pdf.setLineWidth(lw); pdf.line(x1, y, x2, y)
  }

  // ══════════════════════════════════════════════════════════════
  //  HEADER PAGE 1 — dessiné dans la zone 0..HEADER_H_P1
  // ══════════════════════════════════════════════════════════════
  function drawHeader1() {
    // Bande couleur top
    rect(0, 0, PAGE_W, 28, C.primary)

    // Nom société ou logo
    if (doc._logoB64) {
      try {
        pdf.addImage(doc._logoB64, doc._logoFmt || 'JPEG', ML, 4, 0, 20)
      } catch { /* fallback texte */ }
    }
    font('bold', 17); clr(C.white)
    const societeNom = doc.user_societe || `${doc.user_prenom || ''} ${doc.user_nom || ''}`.trim() || 'Mon entreprise'
    tx(doc._logoB64 ? '' : societeNom, ML, 15)

    // Type + numéro
    font('bold', 24)
    tx(typeLabel, PAGE_W - MR, 13, { align: 'right' })
    font('normal', 9)
    tx(`N°\u00A0${doc.numero}`, PAGE_W - MR, 22, { align: 'right' })

    // Émetteur
    let ey = 34
    font('normal', 7.5); clr(C.textMid)
      ;[
        doc.user_adresse,
        [doc.user_cp, doc.user_ville].filter(Boolean).join(' '),
        doc.user_tel,
        doc.user_email,
        doc.user_siret ? `SIRET : ${doc.user_siret}` : null,
        doc.user_tva_intra ? `N° TVA : ${doc.user_tva_intra}` : null,
      ].filter(Boolean).forEach(l => { tx(l, ML, ey); ey += 4 })

    // Bloc client
    const cX = 105
    const cY = 32
    const cW = PAGE_W - MR - cX + 4
    rect(cX - 5, cY - 5, cW + 5, 44, C.primaryLight)
    draw(C.primary); pdf.setLineWidth(0.4)
    pdf.rect(cX - 5, cY - 5, cW + 5, 44, 'S')

    font('bold', 7); clr(C.primary)
    tx('DESTINATAIRE', cX, cY)
    font('bold', 10); clr(C.text)
    tx(doc.contact_nom || '—', cX, cY + 7)

    font('normal', 8.5); clr(C.textMid)
    let cy = cY + 14
      ;[
        doc.contact_adresse,
        [doc.contact_cp, doc.contact_ville].filter(Boolean).join(' '),
        doc.contact_pays !== 'France' ? doc.contact_pays : null,
        doc.contact_email,
        doc.contact_siret ? `SIRET : ${doc.contact_siret}` : null,
        doc.contact_tva_intra ? `N° TVA : ${doc.contact_tva_intra}` : null,
      ].filter(Boolean).forEach(l => { tx(l, cX, cy); cy += 4.5 })

    // Bandeau infos document — positionné à infoY fixe = 92
    const infoY = 88
    rect(ML, infoY, COL_W, 16, C.primaryLight)
    draw(C.border); pdf.setLineWidth(0.2)
    pdf.rect(ML, infoY, COL_W, 16, 'S')

    const infoItems = [
      { label: 'DATE', value: fmtDate(doc.date_document) },
      doc.date_validite ? { label: 'VALIDITÉ', value: fmtDate(doc.date_validite) } : null,
      doc.date_echeance ? { label: 'ÉCHÉANCE', value: fmtDate(doc.date_echeance) } : null,
      doc.reference_client ? { label: 'RÉF. CLIENT', value: doc.reference_client } : null,
      doc.mode_paiement ? { label: 'PAIEMENT', value: doc.mode_paiement } : null,
      { label: 'STATUT', value: STATUTS_FR[doc.statut] || doc.statut || '—' },
    ].filter(Boolean)

    const iW = COL_W / infoItems.length
    infoItems.forEach((item, i) => {
      const ix = ML + i * iW + 3
      font('bold', 6.5); clr(C.primary)
      tx(item.label, ix, infoY + 5)
      if (item.label === 'STATUT') {
        const sBg = STATUT_COLORS[doc.statut] || C.textMid
        rect(ix - 1, infoY + 7, iW - 6, 6, sBg)
        font('bold', 7); clr(C.white)
        tx(item.value, ix + (iW - 6) / 2 - 1, infoY + 11.5, { align: 'center' })
      } else {
        font('bold', 8.5); clr(C.text)
        tx(item.value, ix, infoY + 12)
      }
    })
  }

  // ══════════════════════════════════════════════════════════════
  //  MINI HEADER pages 2+ — dessiné dans la zone 0..HEADER_H_NEXT
  // ══════════════════════════════════════════════════════════════
  function drawHeaderNext() {
    rect(0, 0, PAGE_W, HEADER_H_NEXT - 2, C.primary)
    font('bold', 8); clr(C.white)
    tx(`${typeLabel}\u00A0N°\u00A0${doc.numero}  —  ${doc.contact_nom || ''}`, ML, 10)
    tx(fmtDate(doc.date_document), PAGE_W - MR, 10, { align: 'right' })
  }

  // ══════════════════════════════════════════════════════════════
  //  FOOTER — dessiné dans la zone PAGE_H-MB..PAGE_H
  // ══════════════════════════════════════════════════════════════
  function drawFooter(pageNum, pageCount) {
    const fy = PAGE_H - MB
    rect(0, fy, PAGE_W, MB, C.primary)
    font('normal', 6.5); clr(C.white)

    const piedText = doc.mention_pied || doc.user_pied || ''
    if (piedText) {
      const lines = pdf.splitTextToSize(piedText, COL_W * 0.55)
      tx(lines[0] || '', ML, fy + 8)
    }
    const mentionTVA = doc.user_tva_intra ? `TVA intracommunautaire : ${doc.user_tva_intra}` : ''
    if (mentionTVA) tx(mentionTVA, PAGE_W / 2, fy + 8, { align: 'center' })

    font('bold', 7)
    tx(`Page ${pageNum} / ${pageCount}`, PAGE_W - MR, fy + 8, { align: 'right' })
  }

  // ══════════════════════════════════════════════════════════════
  //  CONSTRUCTION DES LIGNES DU TABLEAU
  // ══════════════════════════════════════════════════════════════
  const lignes = doc.lignes || []
  const tableBody = []

  lignes.forEach(ligne => {
    if (ligne.type_ligne === 'saut') {
      tableBody.push([{
        content: '',
        colSpan: 6,
        styles: { minCellHeight: 5, fillColor: rgb2hex(C.white), lineWidth: 0 },
      }])
      return
    }
    if (ligne.type_ligne === 'texte') {
      tableBody.push([{
        content: ligne.designation || '',
        colSpan: 6,
        styles: {
          fontStyle: 'italic',
          textColor: rgb2hex(C.textMid),
          fontSize: 8,
          fillColor: rgb2hex(C.white),
          lineWidth: 0,
        },
      }])
      return
    }

    // Ligne produit normale
    const remise = parseFloat(ligne.remise_pct) || 0
    tableBody.push([
      { content: ligne.designation || '' },
      { content: String(parseFloat(ligne.quantite) || 1), styles: { halign: 'center' } },
      { content: fmt(ligne.prix_ht), styles: { halign: 'right' } },
      {
        content: remise > 0 ? `${remise}%` : '—',
        styles: {
          halign: 'right',
          textColor: remise > 0 ? rgb2hex(C.danger) : rgb2hex(C.textLight),
          fontStyle: remise > 0 ? 'bold' : 'normal',
        },
      },
      { content: `${parseFloat(ligne.tva_taux) || 0}%`, styles: { halign: 'right' } },
      { content: fmt(ligne.total_ht), styles: { halign: 'right', fontStyle: 'bold' } },
    ])
  })

  // ══════════════════════════════════════════════════════════════
  //  AUTOTABLE — laisse autoTable gérer 100% la pagination
  //
  //  margin.top page 1  = HEADER_H_P1   → tableau commence sous le grand header
  //  margin.top pages + = HEADER_H_NEXT → tableau commence sous le mini header
  //
  //  didDrawPage est appelé AVANT de remplir chaque page →
  //  on dessine header + footer, puis autoTable remplit la zone libre
  // ══════════════════════════════════════════════════════════════
  pdf.autoTable({
    startY: HEADER_H_P1,   // page 1 : commence après le grand header

    margin: {
      top: HEADER_H_NEXT,  // pages 2+ : commence après le mini header
      left: ML,
      right: MR,
      bottom: MB + 2,         // espace pour le footer
    },

    head: [[
      { content: 'DÉSIGNATION', styles: { halign: 'left' } },
      { content: 'QTÉ', styles: { halign: 'center' } },
      { content: 'P.U. HT', styles: { halign: 'right' } },
      { content: 'REMISE', styles: { halign: 'right' } },
      { content: 'TVA', styles: { halign: 'right' } },
      { content: 'TOTAL HT', styles: { halign: 'right' } },
    ]],

    body: tableBody,

    columnStyles: {
      0: { cellWidth: 74 },
      1: { cellWidth: 18, halign: 'center' },
      2: { cellWidth: 28, halign: 'right' },
      3: { cellWidth: 18, halign: 'right' },
      4: { cellWidth: 16, halign: 'right' },
      5: { cellWidth: 28, halign: 'right' },
    },

    headStyles: {
      fillColor: rgb2hex(C.primary),
      textColor: '#ffffff',
      fontStyle: 'bold',
      fontSize: 8,
      cellPadding: { top: 4, bottom: 4, left: 3, right: 3 },
      lineWidth: 0,
    },

    bodyStyles: {
      fontSize: 8.5,
      textColor: rgb2hex(C.text),
      cellPadding: { top: 3, bottom: 3, left: 3, right: 3 },
      lineColor: rgb2hex(C.border),
      lineWidth: 0.15,
    },

    alternateRowStyles: {
      fillColor: rgb2hex(C.rowOdd),
    },

    showHead: 'everyPage',
    rowPageBreak: 'auto',

    // ── didDrawPage : appelé avant de remplir chaque page ──────
    didDrawPage: (data) => {
      if (data.pageNumber === 1) {
        drawHeader1()
      } else {
        drawHeaderNext()
      }
      // Footer : pageCount pas encore connu → on passe 0, corrigé en post-traitement
      drawFooter(data.pageNumber, 0)
    },
  })

  // ── Post-traitement : réécrire les footers avec le bon pageCount ──
  const totalPages = pdf.internal.getNumberOfPages()
  for (let p = 1; p <= totalPages; p++) {
    pdf.setPage(p)
    drawFooter(p, totalPages)
  }

  // ══════════════════════════════════════════════════════════════
  //  BLOC TOTAUX — dessiné après pdf.lastAutoTable.finalY
  // ══════════════════════════════════════════════════════════════
  let y = pdf.lastAutoTable.finalY + 10

  // Nouvelle page si pas assez de place
  if (y + 65 > PAGE_H - MB - 5) {
    pdf.addPage()
    drawHeaderNext()
    drawFooter(pdf.internal.getNumberOfPages(), 0)
    y = HEADER_H_NEXT + 8
  }

  // ── Groupes TVA multi-taux ──────────────────────────────────
  const tvaGroups = {}
  lignes.forEach(l => {
    if (l.type_ligne === 'texte' || l.type_ligne === 'saut') return
    const key = `${parseFloat(l.tva_taux) || 0}%`
    tvaGroups[key] = (tvaGroups[key] || 0) + parseFloat(l.total_tva || 0)
  })
  const isMultiTVA = Object.keys(tvaGroups).length > 1

  const totLines = [
    { label: 'Total HT', value: fmtMoney(doc.total_ht, devise) },
  ]
  if (parseFloat(doc.total_remise) > 0) {
    totLines.push({ label: 'Remise globale', value: `- ${fmtMoney(doc.total_remise, devise)}`, danger: true })
  }
  if (isMultiTVA) {
    Object.entries(tvaGroups).forEach(([taux, montant]) =>
      totLines.push({ label: `TVA ${taux}`, value: fmtMoney(montant, devise) }))
  } else {
    const tauxUnique = Object.keys(tvaGroups)[0] || ''
    totLines.push({ label: tauxUnique ? `TVA (${tauxUnique})` : 'TVA', value: fmtMoney(doc.total_tva, devise) })
  }

  // ── Rendu bloc totaux ───────────────────────────────────────
  const totX = PAGE_W - MR - 74
  const totW = 74
  const totBgH = totLines.length * 8 + 24

  rect(totX - 3, y - 3, totW + 7, totBgH, C.primaryLight)
  draw(C.border); pdf.setLineWidth(0.2)
  pdf.rect(totX - 3, y - 3, totW + 7, totBgH, 'S')

  totLines.forEach(line => {
    font('normal', 8.5)
    clr(line.danger ? C.danger : C.textMid)
    tx(line.label, totX + 2, y + 4)
    clr(line.danger ? C.danger : C.text)
    tx(line.value, totX + totW - 2, y + 4, { align: 'right' })
    hline(y + 7, totX - 3, totX + totW + 4, C.border, 0.15)
    y += 8
  })

  y += 3
  hline(y, totX - 3, totX + totW + 4, C.primary, 0.6)
  y += 4

  rect(totX - 3, y - 1, totW + 7, 14, C.primary)
  font('bold', 12); clr(C.white)
  tx('TOTAL TTC', totX + 2, y + 8)
  tx(fmtMoney(doc.total_ttc, devise), totX + totW - 2, y + 8, { align: 'right' })
  y += 17

  if (parseFloat(doc.montant_regle) > 0) {
    font('normal', 8.5); clr(C.textMid)
    tx('Déjà réglé', totX + 2, y + 4)
    clr(C.accent); font('bold', 8.5)
    tx(fmtMoney(doc.montant_regle, devise), totX + totW - 2, y + 4, { align: 'right' })
    y += 9

    rect(totX - 3, y - 2, totW + 7, 12, C.dangerLight)
    draw(C.danger); pdf.setLineWidth(0.4)
    pdf.rect(totX - 3, y - 2, totW + 7, 12, 'S')
    font('bold', 10); clr(C.danger)
    tx('SOLDE DÛ', totX + 2, y + 6)
    tx(fmtMoney(doc.total_ttc - doc.montant_regle, devise), totX + totW - 2, y + 6, { align: 'right' })
    y += 14
  }

  // ── Conditions ──────────────────────────────────────────────
  if (doc.conditions) {
    const condStartY = pdf.lastAutoTable.finalY + 10
    font('bold', 7.5); clr(C.primary)
    tx('CONDITIONS DE PAIEMENT', ML, condStartY + 4)
    font('normal', 8); clr(C.text)
    const condLines = pdf.splitTextToSize(doc.conditions, totX - ML - 6)
    pdf.text(condLines, ML, condStartY + 12)
  }

  // ── Footer final avec pageCount correct ─────────────────────
  const finalTotal = pdf.internal.getNumberOfPages()
  for (let p = 1; p <= finalTotal; p++) {
    pdf.setPage(p)
    drawFooter(p, finalTotal)
  }

  // ── Sauvegarde ───────────────────────────────────────────────
  const filename = `${doc.numero || 'document'}_${(doc.contact_nom || '').replace(/\s+/g, '_') || 'client'}.pdf`
  if (mode === 'preview') {
    const blobUrl = pdf.output('bloburl')
    window.open(blobUrl, '_blank')
    return blobUrl
  }
  pdf.save(filename)
  return filename
}